/**
 * Project row for list view
 * Story 226: Projects Dashboard
 * Story 464: Added onExport prop and ProjectActions hover menu
 */

import type { ProjectListItem } from '../../hooks/useProjects';
import { StatusDot } from '../workspace/StatusDot';
import { ProjectActions } from './ProjectActions';
import './ProjectRow.css';

interface ProjectRowProps {
  project: ProjectListItem;
  onClick: () => void;
  onExport?: (slug: string) => void;
}

/** Format relative time (e.g., "2 hours ago") */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function ProjectRow({ project, onClick, onExport }: ProjectRowProps) {
  const { stats, recent_users } = project;

  return (
    <button className="project-row" onClick={onClick} type="button">
      {onExport && <ProjectActions projectSlug={project.slug} onExport={onExport} />}
      <div className="project-row__name">
        <span className="project-row__name-text">{project.name}</span>
      </div>

      <div className="project-row__stories">
        <span className="project-row__stat-value">{stats.story_count}</span>
        <span className="project-row__stat-label">stories</span>
      </div>

      <div className="project-row__in-progress">
        <StatusDot status="in-progress" />
        <span className="project-row__stat-value">{stats.in_progress_count}</span>
      </div>

      <div className="project-row__done">
        <StatusDot status="done" />
        <span className="project-row__stat-value">{stats.done_count}</span>
      </div>

      <div className="project-row__users">
        {recent_users.slice(0, 3).map((user, i) => (
          <div
            key={user.github_username || i}
            className="project-row__avatar"
            title={user.github_username || "Unknown"}
          >
            {user.github_username ? (
              <img
                src={`https://github.com/${user.github_username}.png?size=24`}
                alt={user.github_username}
                className="project-row__avatar-img"
              />
            ) : (
              <span className="project-row__avatar-initials">??</span>
            )}
          </div>
        ))}
        {stats.user_count > 3 && (
          <span className="project-row__user-overflow">+{stats.user_count - 3}</span>
        )}
      </div>

      <div className="project-row__updated">
        {formatRelativeTime(project.updated_at)}
      </div>
    </button>
  );
}
